// Port of the image-compression pipeline described in docs/media-compression.md
// (Angular's image-compression.service.ts), adapted to Songhunt's single-file
// visual search. See docs/superpowers/specs/2026-07-21-media-compression-design.md.
//
// Images are downscaled to fit within a bounding box and iteratively re-encoded
// toward a size target; non-canvas-decodable formats (HEIC/HEIF, TIFF) are
// converted first. Videos are NOT handled here - they pass through uncompressed.

// Some browsers report an empty MIME type for HEIC/HEIF and TIFF, so detection
// falls back to the file extension.
const IMAGE_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif', 'tif', 'tiff', 'avif',
];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'wmv', 'flv', 'mkv', 'webm'];

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** True for anything we treat as an image (MIME or extension fallback). */
export function isImage(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  if (file.type) return false; // a non-empty, non-image MIME is authoritative
  return IMAGE_EXTENSIONS.includes(extensionOf(file.name));
}

/** True for anything we treat as a video (MIME or extension allow-list). */
export function isVideo(file: File): boolean {
  if (file.type.startsWith('video/')) return true;
  if (file.type) return false;
  return VIDEO_EXTENSIONS.includes(extensionOf(file.name));
}

/**
 * Which conversion an image needs before the canvas can decode it, or null if
 * the format is browser-native. HEIC/HEIF -> 'heic' (heic2any), TIFF -> 'tiff'
 * (canvas path).
 */
export function needsConversion(file: File): 'heic' | 'tiff' | null {
  const type = file.type.toLowerCase();
  const ext = extensionOf(file.name);
  if (type === 'image/heic' || type === 'image/heif' || ext === 'heic' || ext === 'heif') {
    return 'heic';
  }
  if (type === 'image/tiff' || ext === 'tif' || ext === 'tiff') {
    return 'tiff';
  }
  return null;
}

export interface CompressionOptions {
  maxWidth: number;
  maxHeight: number;
  /** Starting JPEG/WebP quality (0..1). */
  quality: number;
  /** Target max output size, in megabytes. */
  maxSizeInMB: number;
}

/** Default compression settings (design spec). */
export const DEFAULT_COMPRESSION_OPTIONS: CompressionOptions = {
  maxWidth: 1600,
  maxHeight: 1200,
  quality: 0.8,
  maxSizeInMB: 2,
};

/** Minimum JPEG quality floor for the iterative encode loop. */
const MIN_QUALITY = 0.3;
const QUALITY_STEP = 0.1;

// Post-processing hard limits (single source of truth for the app).
export const IMAGE_SIZE_LIMIT = 7 * 1024 * 1024; // 7MB (after compression)
export const VIDEO_SIZE_LIMIT = 100 * 1024 * 1024; // 100MB

/**
 * Validate a processed file against its per-type hard limit. Returns a
 * user-facing error message if it exceeds the limit, or null if it is within
 * bounds (or is neither image nor video).
 */
export function validateSize(file: File): string | null {
  if (isImage(file) && file.size > IMAGE_SIZE_LIMIT) {
    return `Image file is too large. Maximum size is ${IMAGE_SIZE_LIMIT / (1024 * 1024)}MB.`;
  }
  if (isVideo(file) && file.size > VIDEO_SIZE_LIMIT) {
    return `Video file is too large. Maximum size is ${VIDEO_SIZE_LIMIT / (1024 * 1024)}MB.`;
  }
  return null;
}

/**
 * Iteratively encode at decreasing quality until the blob fits `targetBytes`,
 * or the quality floor is reached (then the floor's best-effort blob is
 * returned). `encode` is injected so this decision loop is pure and testable.
 */
export async function encodeToTarget(
  encode: (quality: number) => Promise<Blob>,
  opts: { startQuality: number; minQuality: number; step: number; targetBytes: number }
): Promise<Blob> {
  const { startQuality, minQuality, step, targetBytes } = opts;
  let quality = startQuality;
  let blob = await encode(quality);
  // Step down until it fits or we hit the floor (float-safe comparison).
  while (blob.size > targetBytes && quality - step >= minQuality - 1e-9) {
    quality = quality - step;
    blob = await encode(quality);
  }
  return blob;
}

const MB = 1024 * 1024;

export interface IntakeResult {
  /** The processed file to hand to the store, or null if rejected / no-op. */
  file: File | null;
  /** A user-facing error to toast, or null. */
  error: string | null;
}

/**
 * The single media-intake funnel. All four upload paths (picker, drag-drop,
 * paste-file, paste-screenshot) route a File through here before it reaches the
 * store. Images are compressed; videos pass through; everything is size-checked
 * against the hard limits. `deps.compress` is injected for testing.
 */
export async function intakeMediaFile(
  input: File | undefined,
  opts: CompressionOptions = DEFAULT_COMPRESSION_OPTIONS,
  deps: { compress: (file: File, opts: CompressionOptions) => Promise<File> } = {
    compress: compressImage,
  }
): Promise<IntakeResult> {
  if (!input) return { file: null, error: null };
  if (input.size === 0) {
    return { file: null, error: 'The file selection failed. Please try again.' };
  }

  let processed = input;
  if (isImage(input)) {
    try {
      processed = await deps.compress(input, opts);
    } catch (e) {
      return { file: null, error: (e as Error).message };
    }
  }

  const sizeError = validateSize(processed);
  if (sizeError) return { file: null, error: sizeError };

  return { file: processed, error: null };
}

/**
 * Compress a single image toward the size target. In order:
 *   1. reject a 0-byte file
 *   2. convert HEIC/HEIF (heic2any) or TIFF (canvas) to JPEG if needed
 *   3. skip re-encoding if already within the size target (no quality loss)
 *   4. downscale into a canvas to fit maxWidth x maxHeight
 *   5. iteratively drop quality to the floor to hit the target
 *   6. output PNG-stays-PNG (preserve transparency) else JPEG
 * Returns a File (with a .jpg name when converted/re-encoded to JPEG).
 */
export async function compressImage(file: File, opts: CompressionOptions): Promise<File> {
  if (file.size === 0) {
    throw new Error('The file is empty. Please choose a different image.');
  }

  // 2. Convert non-canvas-decodable formats first.
  let working = file;
  const conversion = needsConversion(file);
  if (conversion === 'heic') {
    working = await convertHeicToJpeg(file);
  } else if (conversion === 'tiff') {
    working = await canvasConvertToJpeg(file);
  }

  // 3. Already small enough -> return as-is (lossless).
  if (working.size <= opts.maxSizeInMB * MB) {
    return working;
  }

  // 4-6. Decode, downscale, iteratively encode.
  const isPng = working.type === 'image/png';
  const outputType = isPng ? 'image/png' : 'image/jpeg';
  const bitmap = await loadImage(working);
  const { width, height } = calculateNewDimensions(
    bitmap.width,
    bitmap.height,
    opts.maxWidth,
    opts.maxHeight
  );
  const canvas = drawToCanvas(bitmap, width, height);

  const blob = await encodeToTarget(
    (quality) => canvasToBlob(canvas, outputType, quality),
    {
      startQuality: opts.quality,
      minQuality: MIN_QUALITY,
      step: QUALITY_STEP,
      targetBytes: opts.maxSizeInMB * MB,
    }
  );

  const outName = isPng ? working.name : withJpgExtension(working.name);
  return new File([blob], outName, { type: outputType });
}

function withJpgExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return (dot >= 0 ? name.slice(0, dot) : name) + '.jpg';
}

// --- Browser IO (not unit-tested in jsdom; exercised via the e2e harness) ---

/** Convert a HEIC/HEIF file to a JPEG File via the heic2any WASM decoder. */
async function convertHeicToJpeg(file: File): Promise<File> {
  try {
    const { default: heic2any } = await import('heic2any');
    const converted = (await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9,
    })) as Blob;
    return new File([converted], withJpgExtension(file.name), { type: 'image/jpeg' });
  } catch {
    throw new Error(
      'Could not convert this HEIC image. Try converting it to JPG or PNG first.'
    );
  }
}

/**
 * Convert a TIFF (or other only-<img>-decodable) file to JPEG via canvas.
 * Tries createImageBitmap first (broader codec support), then an <img> element
 * (which decodes TIFF in Safari).
 */
async function canvasConvertToJpeg(file: File): Promise<File> {
  try {
    const bitmap = await loadImage(file);
    const canvas = drawToCanvas(bitmap, bitmap.width, bitmap.height);
    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.9);
    return new File([blob], withJpgExtension(file.name), { type: 'image/jpeg' });
  } catch {
    throw new Error(
      'Could not convert this image. Try converting it to JPG or PNG first.'
    );
  }
}

interface DecodedImage {
  width: number;
  height: number;
  source: CanvasImageSource;
}

/** Decode a File to something drawable, preferring createImageBitmap. */
async function loadImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return { width: bitmap.width, height: bitmap.height, source: bitmap };
    } catch {
      // fall through to the <img> path
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Image failed to decode'));
      el.src = url;
    });
    return { width: img.naturalWidth, height: img.naturalHeight, source: img };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function drawToCanvas(image: DecodedImage, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas is not available in this browser.');
  }
  ctx.drawImage(image.source, 0, 0, width, height);
  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas encoding failed'))),
      type,
      quality
    );
  });
}

/**
 * Compute the target canvas dimensions to fit (srcW x srcH) inside
 * (maxW x maxH), preserving aspect ratio. Downscale only - an image that
 * already fits is returned unchanged (never upscaled).
 */
export function calculateNewDimensions(
  srcW: number,
  srcH: number,
  maxW: number,
  maxH: number
): { width: number; height: number } {
  const scale = Math.min(maxW / srcW, maxH / srcH, 1);
  return {
    width: Math.round(srcW * scale),
    height: Math.round(srcH * scale),
  };
}
